import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para la configuración de la API key de Voyage AI por
 * profile (M2.3 — RAG sobre PDFs). Sigue el mismo patrón que `anthropic.ts`:
 * `fetch` directo con `credentials: 'include'`, write-only para la apiKey y
 * mapeo de 400 con `issues.fieldErrors`.
 *
 * Voyage es el proveedor de embeddings para nuestra biblioteca de PDFs; la key
 * SÓLO se envía al backend (PUT) y nunca se devuelve por GET por seguridad.
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Modelos soportados
// ---------------------------------------------------------------------------

export const VOYAGE_MODELS = [
  'voyage-3',
  'voyage-3-lite',
  'voyage-large-2',
  'voyage-code-3',
] as const;
export type VoyageModel = (typeof VOYAGE_MODELS)[number];

// ---------------------------------------------------------------------------
// Errores específicos
// ---------------------------------------------------------------------------

/** Backend no tiene API key de Voyage configurada para este profile. */
export class VoyageNotConfiguredError extends ApiError {
  public readonly hint: string;
  constructor(hint = '') {
    super('Voyage no configurado en el backend', 503);
    this.name = 'VoyageNotConfiguredError';
    this.hint = hint;
  }
}

/**
 * Error de validación 400 del backend (mismo shape que Anthropic):
 * `{ error, issues: { fieldErrors, formErrors } }` tras `z.flatten()`.
 */
export class VoyageAppConfigValidationError extends ApiError {
  public readonly fieldErrors: Record<string, string[]>;
  public readonly formErrors: string[];
  constructor(fieldErrors: Record<string, string[]>, formErrors: string[]) {
    super('Validación rechazada por el backend', 400);
    this.name = 'VoyageAppConfigValidationError';
    this.fieldErrors = fieldErrors;
    this.formErrors = formErrors;
  }
}

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

/**
 * Respuesta de `GET /api/voyage/app-config`.
 *
 * Si `configured: false` los demás campos no vienen. NUNCA incluye `apiKey`.
 */
export const VoyageAppConfigSchema = z.object({
  configured: z.boolean(),
  modelDefault: z.enum(VOYAGE_MODELS).optional(),
  updatedAt: z.string().optional(),
});
export type VoyageAppConfig = z.infer<typeof VoyageAppConfigSchema>;

/**
 * Body para `PUT /api/voyage/app-config`. Reglas críticas replicadas en cliente
 * para feedback inmediato:
 *
 * - `apiKey`: formato Voyage (`pa-` + 20+ chars [a-zA-Z0-9-_]).
 * - `modelDefault`: enum de modelos soportados.
 */
export const VoyageAppConfigPutBody = z.object({
  apiKey: z
    .string()
    .min(1, 'Obligatorio')
    .regex(
      /^pa-[a-zA-Z0-9\-_]{20,}$/,
      'Formato de API key de Voyage inválido (debe empezar por pa-)',
    ),
  modelDefault: z.enum(VOYAGE_MODELS, {
    errorMap: () => ({ message: 'Modelo no soportado' }),
  }),
});
export type VoyageAppConfigPutBody = z.infer<typeof VoyageAppConfigPutBody>;

/** Respuesta del PUT: misma shape que GET + flag `apiKeyChanged`. */
export const VoyageAppConfigPutResponseSchema = VoyageAppConfigSchema.extend({
  apiKeyChanged: z.boolean().optional(),
});
export type VoyageAppConfigPutResponse = z.infer<
  typeof VoyageAppConfigPutResponseSchema
>;

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export async function getVoyageAppConfig(): Promise<VoyageAppConfig> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/voyage/app-config`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(
      `Respuesta no OK del backend: ${response.status}`,
      response.status,
    );
  }
  const json = await readJsonSafe(response);
  const parsed = VoyageAppConfigSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      'Respuesta /api/voyage/app-config no cumple esquema',
      response.status,
      parsed.error,
    );
  }
  return parsed.data;
}

export async function putVoyageAppConfig(
  body: VoyageAppConfigPutBody,
): Promise<VoyageAppConfigPutResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/voyage/app-config`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.status === 200) {
    const json = await readJsonSafe(response);
    const parsed = VoyageAppConfigPutResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError(
        'Respuesta /api/voyage/app-config (PUT) no cumple esquema',
        200,
        parsed.error,
      );
    }
    return parsed.data;
  }

  if (response.status === 400) {
    const json = await readJsonSafe(response);
    const issues =
      json && typeof json === 'object' && 'issues' in json
        ? (json as { issues?: unknown }).issues
        : null;
    const fieldErrors: Record<string, string[]> = {};
    const formErrors: string[] = [];
    if (issues && typeof issues === 'object') {
      const i = issues as { fieldErrors?: unknown; formErrors?: unknown };
      if (i.fieldErrors && typeof i.fieldErrors === 'object') {
        for (const [k, v] of Object.entries(
          i.fieldErrors as Record<string, unknown>,
        )) {
          if (Array.isArray(v)) {
            fieldErrors[k] = v.filter((x): x is string => typeof x === 'string');
          }
        }
      }
      if (Array.isArray(i.formErrors)) {
        for (const v of i.formErrors) {
          if (typeof v === 'string') formErrors.push(v);
        }
      }
    }
    throw new VoyageAppConfigValidationError(fieldErrors, formErrors);
  }

  throw new ApiError(
    `Respuesta no OK del backend: ${response.status}`,
    response.status,
  );
}

export async function deleteVoyageAppConfig(): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/voyage/app-config`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status === 204 || response.ok) return;
  throw new ApiError(
    `Respuesta no OK del backend: ${response.status}`,
    response.status,
  );
}

// ---------------------------------------------------------------------------
// Hooks (TanStack Query)
// ---------------------------------------------------------------------------

export const VOYAGE_APP_CONFIG_QUERY_KEY = ['voyage', 'app-config'] as const;

export function useVoyageAppConfig(): UseQueryResult<VoyageAppConfig, Error> {
  return useQuery<VoyageAppConfig, Error>({
    queryKey: VOYAGE_APP_CONFIG_QUERY_KEY,
    queryFn: getVoyageAppConfig,
    staleTime: 0,
    retry: 1,
  });
}

export function useUpdateVoyageAppConfig(): UseMutationResult<
  VoyageAppConfigPutResponse,
  Error,
  VoyageAppConfigPutBody
> {
  const qc = useQueryClient();
  return useMutation<VoyageAppConfigPutResponse, Error, VoyageAppConfigPutBody>({
    mutationFn: (body) => putVoyageAppConfig(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: VOYAGE_APP_CONFIG_QUERY_KEY });
    },
  });
}

export function useDeleteVoyageAppConfig(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => deleteVoyageAppConfig(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: VOYAGE_APP_CONFIG_QUERY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Helpers expuestos a la UI
// ---------------------------------------------------------------------------

/** Etiqueta legible de cada modelo Voyage, para el selector. */
export function voyageModelLabel(model: VoyageModel): string {
  switch (model) {
    case 'voyage-3':
      return 'voyage-3 (recomendado, equilibrio calidad/coste)';
    case 'voyage-3-lite':
      return 'voyage-3-lite (más barato y rápido)';
    case 'voyage-large-2':
      return 'voyage-large-2 (máxima calidad, más caro)';
    case 'voyage-code-3':
      return 'voyage-code-3 (optimizado para código/técnico)';
  }
}
